import type { Node } from '@xyflow/react';
import type { RelationshipType } from '../../app/api/generated/client';
import { connectorNodeSize, connectorNodeSpacer, horizontalRelationships, nodeSpacing, partnerNodeSize } from '../config/constants';
import type { ConnectorNodeData, FamilyTreeEdgeType, FamilyTreeNode } from '../interfaces';
import { NodeHandlerId } from '../types/node-handler-id';
import { isNodeIdInEdge, isVerticalEdge } from './edge-utils';
import { getRectsFromNodes, isConnectorNode, isPartnerNode } from './node-utils';
import { resolveNodeOverlaps } from './resolve-node-overlaps';

/**
 * Move all connector-nodes one layer up, because ELK treats all source-target connections as parent-child relations.
 * Once the connector-nodes are on the same layer as their spouses:
 * - move the spouses in proper distance next to their respective connector-nodes
 * - check if there are more spouses/connector-nodes connected to these spouses and prioritize the "married" over "divorced" for positioning
 */
const moveConnectorNodesAndSpouses = (nodes: FamilyTreeNode[], edges: FamilyTreeEdgeType[]) => {
    const getSpouseNode = (side: 'left' | 'right', nodeId: string): FamilyTreeNode | undefined => {
        const spouseSourceHandle: NodeHandlerId = side === 'left' ? NodeHandlerId.SOURCE_LEFT : NodeHandlerId.SOURCE_RIGHT;
        const spouseTargetHandle: NodeHandlerId = side === 'left' ? NodeHandlerId.TARGET_LEFT : NodeHandlerId.TARGET_RIGHT;

        const spouseEdge = edges.find(
            (edge) =>
                (edge.source === nodeId && edge.sourceHandle === spouseSourceHandle) || (edge.target === nodeId && edge.targetHandle === spouseTargetHandle),
        );
        const spouseId = spouseEdge?.source === nodeId ? spouseEdge.target : spouseEdge?.source;

        return nodes.find((node) => node.id === spouseId);
    };

    const isPriorityConnection = (connectorNode: Node<ConnectorNodeData>): boolean => connectorNode.data.relationshipType !== 'IS_DIVORCED_FROM';

    const hasMoreConnectorNodeRelations = (connectorNodeId: string, spouseNodeId: string): boolean => {
        const spouseEdges = edges.filter(isNodeIdInEdge(spouseNodeId));

        return spouseEdges?.some((edge) => edge.source !== connectorNodeId && edge.target !== connectorNodeId);
    };

    nodes.filter(isConnectorNode).forEach((connectorNode: Node<ConnectorNodeData>) => {
        const spouseNodeLeft = getSpouseNode('left', connectorNode.id);
        const spouseNodeRight = getSpouseNode('right', connectorNode.id);
        const targetY = (spouseNodeLeft?.position.y || spouseNodeRight?.position.y) ?? connectorNode.position.y;

        if (spouseNodeLeft && (isPriorityConnection(connectorNode) || !hasMoreConnectorNodeRelations(connectorNode.id, spouseNodeLeft.id))) {
            spouseNodeLeft.position = { x: connectorNode.position.x - partnerNodeSize.width - connectorNodeSpacer, y: targetY };
        }
        if (spouseNodeRight && (isPriorityConnection(connectorNode) || !hasMoreConnectorNodeRelations(connectorNode.id, spouseNodeRight.id))) {
            spouseNodeRight.position = {
                x: connectorNode.position.x + partnerNodeSize.width + connectorNodeSpacer - connectorNodeSize.width / 2,
                y: targetY,
            };
        }

        connectorNode.position = { x: connectorNode.position.x, y: targetY + partnerNodeSize.height / 2 - connectorNodeSize.height / 2 + 1 };
    });
};

/**
 * After the connector-nodes and their spouses are properly positioned and aligned:
 * - get their children and move these nodes one layer up as well
 * - get all related nodes of the children and move them up one layer
 * - do this recursively to ensure all nodes connected to the connector-nodes children are moved 1 layer up
 */
const moveConnectorNodeChildren = (nodes: FamilyTreeNode[], edges: FamilyTreeEdgeType[]) => {
    const getRelatedNodes = (nodeId: string, relationshipTypes?: RelationshipType[], excludedId?: string): FamilyTreeNode[] => {
        const isChildNodeNeeded = (edgeRelationshipType?: RelationshipType) =>
            relationshipTypes?.includes('IS_PARENT_OF') && edgeRelationshipType === 'IS_PARENT_OF';
        const isSpouseNodeNeeded = (edgeRelationshipType?: RelationshipType) =>
            relationshipTypes?.includes('IS_SPOUSE_OF') ||
            relationshipTypes?.includes('IS_REGISTERED_PARTNER_OF') ||
            (relationshipTypes?.includes('IS_DIVORCED_FROM') && edgeRelationshipType && horizontalRelationships.includes(edgeRelationshipType));
        const relatedEdges = edges
            .filter(isNodeIdInEdge(nodeId))
            .filter((edge) => !relationshipTypes?.length || isChildNodeNeeded(edge.data?.relationshipType) || isSpouseNodeNeeded(edge.data?.relationshipType));

        return nodes.filter((node) => ![nodeId, excludedId].includes(node.id) && relatedEdges.some(isNodeIdInEdge(node.id)));
    };

    const moveNodesOneLayerUp = (nodesToMove: FamilyTreeNode[], connectorNodeId: string, visited = new Set<string>()) => {
        if (!nodesToMove?.length) return;

        for (const node of nodesToMove) {
            if (visited.has(node.id)) continue;
            visited.add(node.id);

            node.position = { ...node.position, y: node.position.y - nodeSpacing.y };

            const relatedNodes = getRelatedNodes(node.id, undefined, connectorNodeId);
            if (relatedNodes?.length) {
                moveNodesOneLayerUp(relatedNodes, node.id, visited);
            }
        }
    };

    nodes.filter(isConnectorNode).forEach((connectorNode) => {
        const childNodes = getRelatedNodes(connectorNode.id, ['IS_PARENT_OF']);
        moveNodesOneLayerUp(childNodes, connectorNode.id);
    });
};

/**
 * Resolve X-axis overlaps within same Y-layer and recenter connector nodes.
 */
function fixXOverlapsAndCenterConnectors(nodes: FamilyTreeNode[]) {
    const rects = getRectsFromNodes(nodes);
    resolveNodeOverlaps(rects, 60, 10);

    // Apply positions back to nodes
    rects.forEach((rect) => {
        const node = nodes.find((n) => n.id === rect.id);
        if (node) node.position = { x: rect.x, y: rect.y };
    });
}

/**
 * Align nodes with only one parent, centering on their child, if no other node is in the way.
 */
const alignSingleParentNodes = (nodes: FamilyTreeNode[], edges: FamilyTreeEdgeType[]): FamilyTreeNode[] => {
    const singleParentNodes = nodes.filter(isPartnerNode).filter((node) => {
        const relatedVerticalEdges = edges.filter(isVerticalEdge).filter(isNodeIdInEdge(node.id));
        const relatedNodeIds = relatedVerticalEdges.map((edge) => (edge.source === node.id ? edge.target : edge.source));

        return nodes.some((n) => !n.data.isConnector && relatedNodeIds.includes(n.id) && n.position.y > node.position.y);
    });

    const isRectOverlapping = (x: number, y: number, nodeId: string): boolean =>
        nodes.some((other) => {
            if (other.id === nodeId) return false;

            const xOverlap = Math.abs(other.position.x - x) < partnerNodeSize.width;
            const yOverlap = Math.abs(other.position.y - y) < partnerNodeSize.height;

            return xOverlap && yOverlap;
        });

    nodes.forEach((node) => {
        if (singleParentNodes.some((n) => n.id === node.id)) {
            const singleParentEdge = edges.filter(isVerticalEdge).filter(isNodeIdInEdge(node.id))[0];
            const relatedChildNode = nodes.find((n) => n.id === singleParentEdge[singleParentEdge.source === node.id ? 'target' : 'source']);

            const newXPosition = relatedChildNode?.position.x ?? node.position.x;
            if (isRectOverlapping(newXPosition, node.position.y, node.id)) return;

            node.position.x = newXPosition;
        }
    });

    return nodes;
};

/**
 * This util function is used to do all necessary optimizations on the family-tree nodes positions.
 * Unfortunately, ELK does not completely offer the positioning calculation the family-tree needs.
 *
 * That is why after the initial positioning by ELK, this hook gives the final touch to the family-tree:
 * - Move connector-nodes one layer up (ELK treats spouse connections as parent-child relations)
 * - Move spouses of the connector node next to each other properly (prioritize married over divorced)
 * - Move all children (and their spouses and children) recursively one layer upd.
 * - Align single-parent nodes with their respective children (on x-axis)
 * - Finally, fix all overlapping nodes by moving them to the right (layer by layer) and then center the connector-nodes of those moved spouses
 *
 * @param {FamilyTreeNode[]} nodes All nodes with the positions calculated by ELK
 * @param {FamilyTreeEdgeType[]} edges All edges connecting the nodes also passed through the ELK layout calculation
 */
export const optimizeNodePositions = (nodes: FamilyTreeNode[] = [], edges: FamilyTreeEdgeType[] = []): FamilyTreeNode[] => {
    // Move all connector-nodes 1 layer up and position their spouse nodes properly on the sides
    moveConnectorNodesAndSpouses(nodes, edges);

    // Recursively move all Nodes connected to the connector node's children up
    moveConnectorNodeChildren(nodes, edges);

    // Iterate through the layers (along y-axis): move all overlapping nodes to the right and center the connector-nodes between the spouses
    fixXOverlapsAndCenterConnectors(nodes);

    // Align single parent and child nodes (meaning parent/child nodes without connector-nodes)
    return alignSingleParentNodes(nodes, edges);
};
